import {
  generateIdentityKeyPair,
  generatePrfSalt,
  toB64,
  unwrapPrivateKey,
  wrapPrivateKey,
} from './chatCrypto';
import type { KeyWrap, WrappedPrivateKey } from './chatCrypto';
import { getMyKeys, publishKeys } from './chatService';
import type { PublishKeysBody, UserKeys } from './chatService';
import type { RegistrationResponseJSON } from '@simplewebauthn/browser';

/**
 * In-memory key session for the researcher chat plus the recovery-key format.
 *
 * The unwrapped private key is held ONLY here, as the raw 32 bytes in a
 * `Uint8Array` that is zero-filled on lock and logout. It is never written to
 * localStorage, sessionStorage, or IndexedDB in unwrapped form; the trusted
 * browser keeps a device-key-wrapped copy (chatTrustedBrowser.ts) and the
 * server holds passkey- and recovery-key-wrapped copies.
 */

/** How the current session was unlocked; drives the settings gate order. */
export type UnlockMethod = 'setup' | 'passkey' | 'device' | 'recovery';

export interface SessionKeys {
  publicKey: string;
  /** The live raw bytes — a reference, not a copy, so `lockKeys()` zero-fills every holder. */
  privateKey: Uint8Array;
  unlockedWith: UnlockMethod;
}

let session: SessionKeys | null = null;

/** Hold an unlocked private key for this session (any previous key is zero-filled first). */
export function holdSessionKeys(
  publicKey: string,
  privateKey: Uint8Array,
  unlockedWith: UnlockMethod,
): void {
  lockKeys();
  session = { publicKey, privateKey, unlockedWith };
}

/** The unlocked session, or null while locked. Memory only — see module comment. */
export function getSessionKeys(): SessionKeys | null {
  return session;
}

/** Zero-fill and drop the in-memory private key (idle lock / logout). */
export function lockKeys(): void {
  if (!session) return;
  session.privateKey.fill(0);
  session = null;
}

// ---------------------------------------------------------------------------
// Recovery key: 160 random bits, Crockford base32, 32 characters in 8 groups of 4
// ---------------------------------------------------------------------------

export const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const RECOVERY_KEY_LENGTH = 32;
export const RECOVERY_KEY_FILENAME = 'StudySync-chat-recovery-key.txt';
export const RECOVERY_KEY_MISMATCH_MESSAGE =
  'That recovery key does not match. Check the file StudySync-chat-recovery-key.txt.';

/** 20 random bytes → 32 Crockford base32 characters (no I, L, O, U). */
export function generateRecoveryKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(20));
  let bits = 0;
  let value = 0;
  let out = '';
  bytes.forEach((byte) => {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += CROCKFORD_ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  });
  return out;
}

/** The display/file form: 8 groups of 4 separated by single spaces. */
export function formatRecoveryKey(key: string): string {
  return (key.match(/.{1,4}/g) ?? []).join(' ');
}

/**
 * Normalize typed or pasted input: trim, drop separators (whitespace and
 * hyphens), upper-case. No alias substitution — a character outside the
 * 32-symbol alphabet, or a length other than 32, is a mismatch (`null`) and
 * PBKDF2 never runs for it.
 */
export function normalizeRecoveryKey(input: string): string | null {
  const key = input.trim().replace(/[\s-]/g, '').toUpperCase();
  if (key.length !== RECOVERY_KEY_LENGTH) return null;
  for (let i = 0; i < key.length; i++) {
    if (!CROCKFORD_ALPHABET.includes(key[i])) return null;
  }
  return key;
}

function isoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The exact four-line body of StudySync-chat-recovery-key.txt. */
export function recoveryKeyFileBody(key: string, email: string, createdAt: Date = new Date()): string {
  return [
    `StudySync chat recovery key for ${email} (created ${isoDate(createdAt)})`,
    formatRecoveryKey(key),
    'Keep this file somewhere safe. It is the only way to unlock chat if you lose your passkeys.',
    'Never share it. StudySync staff will never ask for it.',
  ].join('\n');
}

/** Trigger a browser download of the recovery-key file. */
export function downloadRecoveryKeyFile(body: string): void {
  const blob = new Blob([body], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = RECOVERY_KEY_FILENAME;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** Wrap the private key under a (normalized) recovery key: PBKDF2 password = the 32-character string. */
export async function wrapForRecoveryKey(
  privateKey: Uint8Array,
  recoveryKey: string,
): Promise<WrappedPrivateKey> {
  return wrapPrivateKey(privateKey, recoveryKey);
}

export type RecoveryUnlockResult = { status: 'unlocked'; privateKey: Uint8Array } | { status: 'mismatch' };

/** Unwrap `wrappedPrivateKey` with typed/pasted recovery-key input (normalized first). */
export async function unwrapWithRecoveryKey(
  wrappedPrivateKey: WrappedPrivateKey,
  input: string,
): Promise<RecoveryUnlockResult> {
  const key = normalizeRecoveryKey(input);
  if (!key) return { status: 'mismatch' };
  try {
    return { status: 'unlocked', privateKey: await unwrapPrivateKey(wrappedPrivateKey, key) };
  } catch {
    return { status: 'mismatch' };
  }
}

// ---------------------------------------------------------------------------
// Identity setup / reset pipeline pieces (the dialogs decide the step order)
// ---------------------------------------------------------------------------

/** Material generated in-browser for a new identity key; zero-filled by `discardPendingIdentity`. */
export interface PendingIdentity {
  publicKey: string;
  privateKey: Uint8Array;
  prfSalt: Uint8Array;
}

export async function createPendingIdentity(): Promise<PendingIdentity> {
  const keyPair = await generateIdentityKeyPair();
  return { publicKey: keyPair.publicKey, privateKey: keyPair.privateKey, prfSalt: generatePrfSalt() };
}

export function discardPendingIdentity(identity: PendingIdentity | null): void {
  if (!identity) return;
  identity.privateKey.fill(0);
  identity.prfSalt.fill(0);
}

export interface PasskeyMaterial {
  name: string;
  registration: RegistrationResponseJSON;
  wrap: KeyWrap;
}

/** Build the exact `POST /api/chat/keys` body. */
export function buildPublishBody(
  identity: PendingIdentity,
  wrappedPrivateKey: WrappedPrivateKey,
  passkey: PasskeyMaterial,
): PublishKeysBody {
  return {
    publicKey: identity.publicKey,
    wrappedPrivateKey,
    prfSalt: toB64(identity.prfSalt),
    passkey: { name: passkey.name, registration: passkey.registration, wrap: passkey.wrap },
  };
}

/**
 * Upload the identity. Before retrying a failed upload the caller checks
 * `publishLanded` — a 200 from `GET /keys/me` whose `publicKey` equals the
 * generated key means the earlier publish landed.
 */
export async function publishIdentity(body: PublishKeysBody): Promise<UserKeys> {
  return publishKeys(body);
}

export async function publishLanded(publicKey: string): Promise<UserKeys | null> {
  const keys = await getMyKeys();
  return keys && keys.publicKey === publicKey ? keys : null;
}
